import { useEffect, useState } from 'react';
import { Play, Eye, Clock } from 'lucide-react';
import { AlertModal } from '../../components/AlertModal';
import { ConfirmModal } from '../../components/ConfirmModal';

const API = import.meta.env.VITE_API_URL;

export default function AffiliateCalc() {
  const [defaultLookBack, setDefaultLookBack] = useState(3);
  const [maxLookBack, setMaxLookBack] = useState(12);
  const [lookBack, setLookBack] = useState(3);
  const [calcDate, setCalcDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCalcAt, setLastCalcAt] = useState<string | null>(null);
  const [lastCronAt, setLastCronAt] = useState<string | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<any>(null);

  useEffect(() => {
    fetch(`${API}/admin/affiliates/calc/settings`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any }>)
      .then((j) => {
        if (j.success) {
          setDefaultLookBack(j.data.default_look_back_months);
          setLookBack(j.data.default_look_back_months);
          setMaxLookBack(j.data.max_look_back_months);
        }
      });
    fetch(`${API}/admin/affiliates/calc/last-run`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any }>)
      .then((j) => {
        if (j.success) {
          setLastCalcAt(j.data.last_calc_run_at);
          setLastCronAt(j.data.last_cron_run_at);
        }
      });
  }, []);

  const doPreview = async () => {
    setError(null);
    setPreview(null);
    setRunResult(null);
    const res = await fetch(`${API}/admin/affiliates/calc/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ calc_date: calcDate, look_back_months: lookBack }),
    });
    const json = (await res.json()) as { success: boolean; data?: any; error?: string };
    if (!json.success) {
      setError(json.error || 'Preview failed');
      return;
    }
    setPreview(json.data);
  };

  const doRun = () => {
    if (!preview) {
      setError('Run Preview first.');
      return;
    }
    const overall = preview.overall_totals || {};
    const newCount = overall.new_period_count || 0;
    const adjCount = overall.adjustment_count || 0;
    const totalDue = Number(overall.total_due || 0).toFixed(2);
    const negBalances = (preview.per_affiliate || []).filter((a: any) => Number(a.running_balance_after || 0) < 0);
    const negNote = negBalances.length > 0
      ? `\n\n⚠ ${negBalances.length} affiliate(s) will have NEGATIVE balances after this run: ${negBalances.map((a: any) => `${a.display_name} (${Number(a.running_balance_after).toFixed(2)})`).join(', ')}`
      : '';
    setConfirmMessage(`Apply: ${newCount} new period(s) + ${adjCount} adjustment(s), total $${totalDue}.${negNote}`);
  };

  const executeRun = async () => {
    setConfirmMessage(null);
    if (!preview) return;
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/affiliates/calc/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calc_date: calcDate,
          look_back_months: lookBack,
          preview_token: preview.preview_token,
        }),
      });
      const json = (await res.json()) as { success: boolean; data?: any; error?: string };
      if (!json.success) {
        setError(json.error || 'Run failed');
        return;
      }
      setRunResult(json.data);
      setPreview(null);
    } catch (e) {
      setError(`Could not reach the server: ${String(e)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Calculate Commissions</h2>

      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs text-gray-600">Calc Date</label>
            <input type="date" value={calcDate} onChange={(e) => setCalcDate(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
            <div className="text-xs text-gray-500 mt-1">Calculable month = month before this date.</div>
          </div>
          <div>
            <label className="text-xs text-gray-600">Look-back Months</label>
            <input
              type="number"
              min={1}
              max={maxLookBack}
              value={lookBack}
              onChange={(e) => setLookBack(parseInt(e.target.value) || 1)}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
            <div className="text-xs text-gray-500 mt-1">Default {defaultLookBack}, max {maxLookBack}.</div>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={doPreview} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200">
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button
              onClick={doRun}
              disabled={!preview || running}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Play className="w-4 h-4" /> {running ? 'Running...' : 'Run Calc Now'}
            </button>
          </div>
        </div>

        <div className="flex gap-6 text-xs text-gray-500 pt-3 border-t">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last calc run: {lastCalcAt ? new Date(lastCalcAt).toLocaleString() : 'Never'}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last CRON run: {lastCronAt ? new Date(lastCronAt).toLocaleString() : 'Never'}
          </div>
          <div className="flex-1" />
          <div>Monthly CRON: 15th @ 09:00 UTC (look-back=1)</div>
        </div>
      </div>

      {runResult && (
        <div className="bg-green-50 text-green-800 p-4 rounded-lg mb-4">
          <div className="font-medium">Run complete.</div>
          <div className="text-sm mt-1">
            Created {runResult.new_periods_created} new period(s), posted {runResult.adjustments_posted} adjustment(s).
            Total applied: ${Number(runResult.total_amount_applied).toFixed(2)}.
          </div>
        </div>
      )}

      {preview && <PreviewDisplay preview={preview} />}

      <AlertModal isOpen={!!error} title="Calc error" message={error || ''} onClose={() => setError(null)} />
      <ConfirmModal
        isOpen={!!confirmMessage}
        title="Run Calc Now"
        message={confirmMessage || ''}
        confirmLabel="Apply"
        onConfirm={executeRun}
        onCancel={() => setConfirmMessage(null)}
      />
    </div>
  );
}

function PreviewDisplay({ preview }: { preview: any }) {
  const overall = preview.overall_totals || {};
  const perAffiliate = preview.per_affiliate || [];

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="font-medium text-blue-900">Preview</div>
        <div className="text-sm text-blue-800 mt-1">
          Scope: {preview.scope_window_start} to {preview.scope_window_end} ({preview.look_back_months} month look-back).
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
          <div>
            <div className="text-xs text-blue-600">New Periods</div>
            <div className="text-xl font-bold">{overall.new_period_count || 0}</div>
            <div className="text-xs">${Number(overall.total_new_amount || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs text-blue-600">Adjustments</div>
            <div className="text-xl font-bold">{overall.adjustment_count || 0}</div>
            <div className="text-xs">${Number(overall.total_adjustment_amount || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs text-blue-600">Affiliates Affected</div>
            <div className="text-xl font-bold">{perAffiliate.length}</div>
          </div>
          <div>
            <div className="text-xs text-blue-600">Total Due</div>
            <div className="text-xl font-bold">${Number(overall.total_due || 0).toFixed(2)}</div>
          </div>
        </div>
      </div>

      {perAffiliate.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Nothing to calculate. No new periods or adjustments needed.
        </div>
      ) : (
        <div className="space-y-3">
          {perAffiliate.map((a: any) => (
            <div key={a.affiliate_id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-medium">{a.display_name}</div>
                  <div className="text-xs text-gray-500">{a.status}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Net change</div>
                  <div className={`text-lg font-mono font-bold ${a.net_change < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    ${Number(a.net_change).toFixed(2)}
                  </div>
                  <div className={`text-xs ${a.running_balance_after < 0 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                    Balance after: ${Number(a.running_balance_after).toFixed(2)}
                  </div>
                </div>
              </div>

              {a.new_periods?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-gray-700 mb-1">New Periods ({a.new_periods.length})</div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr><th className="text-left p-1">Period</th><th className="text-right p-1">Gross</th><th className="text-right p-1">Tier Rate</th><th className="text-right p-1">Total</th><th className="text-left p-1">Lines</th></tr>
                    </thead>
                    <tbody>
                      {a.new_periods.map((p: any, i: number) => (
                        <tr key={i} className="border-b">
                          <td className="p-1">{p.period_start}</td>
                          <td className="p-1 text-right font-mono">${Number(p.gross_referral_revenue).toFixed(2)}</td>
                          <td className="p-1 text-right">{p.tier_rate_applied}%</td>
                          <td className="p-1 text-right font-mono font-bold">${Number(p.total_due).toFixed(2)}</td>
                          <td className="p-1">{p.line_items?.length || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {a.adjustments?.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-700 mb-1">Adjustments ({a.adjustments.length})</div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr><th className="text-left p-1">Original Period</th><th className="text-right p-1">Was</th><th className="text-right p-1">Should be</th><th className="text-right p-1">Delta</th></tr>
                    </thead>
                    <tbody>
                      {a.adjustments.map((adj: any, i: number) => (
                        <tr key={i} className="border-b">
                          <td className="p-1">{adj.period_being_adjusted}</td>
                          <td className="p-1 text-right font-mono">${Number(adj.original_total).toFixed(2)}</td>
                          <td className="p-1 text-right font-mono">${Number(adj.would_be_total).toFixed(2)}</td>
                          <td className={`p-1 text-right font-mono font-bold ${adj.delta < 0 ? 'text-red-600' : 'text-green-700'}`}>
                            {adj.delta < 0 ? '' : '+'}${Number(adj.delta).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
